/** @file Direction-set schema shared by the document and the rig JSON (count, layout4, entries, layout rule). */
import { z } from 'zod';
import { names } from '../../names';

/** Directions a 4-set may use per layout; an 8-set may use all of `names.DIRS`. */
const LAYOUT4_DIRS = { diagonal: ['NE', 'SE', 'SW', 'NW'], cardinal: ['N', 'E', 'S', 'W'] } as const;

/** Builds the set schema around one entry schema (doc entries hold ids, rig entries hold indices). */
export function directionSet<E extends z.ZodType>(entry: E) {
  return z
    .object({
      count: z.union([z.literal(4), z.literal(8)]),
      layout4: z.enum(['diagonal', 'cardinal']).optional(),
      /** Iso tile size [w, h] (px) that skews the diagonal sector centres. Authoritative when present; the editor's `direction.syncIso` command copies `view.grid`, and the compiler copies this field (falling back to `view.grid` only when absent and the grid is iso). */
      iso: z.tuple([z.number().positive(), z.number().positive()]).optional(),
      /** Only the directions the set defines (a 4-set has at most 4 entries). */
      entries: z.partialRecord(z.enum(names.DIRS), entry),
    })
    .strict()
    .superRefine((set, ctx) => {
      const allowed: readonly string[] = set.count === 8 ? names.DIRS : LAYOUT4_DIRS[set.layout4 ?? 'diagonal'];
      for (const dir of Object.keys(set.entries)) {
        if (!allowed.includes(dir)) ctx.addIssue({ code: 'custom', path: ['entries', dir], message: `${dir} is not part of a ${String(set.count)}-direction ${set.layout4 ?? 'diagonal'} set` });
      }
    });
}
